import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  getIdToken
} from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { auth, googleProvider, githubProvider, appleProvider, db } from '../firebase/config';
import ApiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from backend
  const fetchUserProfile = async (userId) => {
    try {
      // Use userId parameter or currentUser
      const uid = userId || currentUser?.uid;
      if (!uid) {
        setUserProfile(null);
        return null;
      }

      const user = userId ? auth.currentUser : currentUser;
      const token = await user.getIdToken();
      
      console.log("Fetching profile for userId:", uid);
      const response = await ApiService.getUserProfile(uid, token);
      console.log("User profile response:", response);
      
      // ApiService returns data directly, not wrapped in .data
      setUserProfile(response);
      return response;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
      return null;
    }
  };

  // Sign up with email and password
  const signup = async (email, password, displayName = '', sendFirebaseVerification = false) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Send Firebase email verification link (optional)
    // Note: We use backend verification codes instead, so this is disabled by default
    if (sendFirebaseVerification) {
      await sendEmailVerification(userCredential.user);
    }
    
    // Create user document in Firestore with emailVerified status
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userDocRef, {
      uid: userCredential.user.uid,
      email: email,
      displayName: displayName,
      emailVerified: false,
      createdAt: new Date().toISOString(),
      photoURL: userCredential.user.photoURL || null
    });
    
    return userCredential;
  };
  
  // Send verification email manually
  const sendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      return sendEmailVerification(auth.currentUser);
    }
    throw new Error('No user logged in or email already verified');
  };
  
  // Check and update email verification status in Firestore
  const checkEmailVerification = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload(); // Refresh user data from Firebase
      const isVerified = auth.currentUser.emailVerified;
      
      if (isVerified) {
        // Update Firestore
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, {
          emailVerified: true,
          emailVerifiedAt: new Date().toISOString()
        });
      }
      
      return isVerified;
    }
    return false;
  };

  // Sign in with email and password
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign out
  const logout = () => {
    setUserProfile(null);
    return signOut(auth);
  };

  // Reset password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Social login methods
  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    await handleSocialLoginUser(result.user);
    return result;
  };

  const signInWithGithub = async () => {
    const result = await signInWithPopup(auth, githubProvider);
    await handleSocialLoginUser(result.user);
    return result;
  };

  const signInWithApple = async () => {
    const result = await signInWithPopup(auth, appleProvider);
    await handleSocialLoginUser(result.user);
    return result;
  };
  
  // Handle social login user creation/update in Firestore
  const handleSocialLoginUser = async (user) => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const usersRef = collection(db, 'users');
      const userQuery = query(usersRef, where('uid', '==', user.uid));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        // Create new user document for social login
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          emailVerified: true, // Social providers verify emails
          createdAt: new Date().toISOString(),
          photoURL: user.photoURL || null,
          provider: user.providerData[0]?.providerId || 'unknown'
        });
      } else {
        // Update existing user with verification status
        await updateDoc(userDocRef, {
          emailVerified: true,
          lastLogin: new Date().toISOString(),
          photoURL: user.photoURL || null
        });
      }
    } catch (error) {
      console.error('Error handling social login user:', error);
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return userProfile?.role.toLowerCase() === 'admin';
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch user profile when user is authenticated
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    isAdmin,
    login,
    signup,
    logout,
    resetPassword,
    sendVerificationEmail,
    checkEmailVerification,
    signInWithGoogle,
    signInWithGithub,
    signInWithApple,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 